
import { AggregatedSessionV2 } from "@global/types/session";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import React, { useState } from "react";
import { formatDuration, getExperiments, getPageFlow } from "./helpers";

// Color palette for pages (cycling through)
const PAGE_COLORS = [
  "#2196f3", // blue
  "#4caf50", // green
  "#ff9800", // orange
  "#9c27b0", // purple
  "#00bcd4", // cyan
  "#f44336", // red
  "#795548", // brown
  "#607d8b", // blue-grey
];

interface SessionDetailDialogProps {
  session: AggregatedSessionV2 | null;
  onClose: () => void;
  onDelete?: (sessionId: string) => Promise<void>;
}

export const SessionDetailDialog: React.FC<SessionDetailDialogProps> = ({
  session,
  onClose,
  onDelete,
}) => {
  const [deleting, setDeleting] = useState(false);

  if (!session) return null;

  const handleDelete = async () => {
    if (!onDelete || !session) return;

    const confirmed = window.confirm(
      "Are you sure you want to delete this session? This action cannot be undone."
    );
    if (!confirmed) return;

    setDeleting(true);
    try {
      await onDelete(session.sessionId);
      onClose();
    } catch (error) {
      console.error("Failed to delete session:", error);
      alert("Failed to delete session. Please try again.");
    } finally {
      setDeleting(false);
    }
  };

  const sessionStartTime = session.sessionStart;
  const totalDurationMs = session.sessionEnd - session.sessionStart;

  // Build a map of page paths to colors
  const pageColorMap = new Map<string, string>();
  session.pageSessions.forEach((ps) => {
    if (!pageColorMap.has(ps.pagePath)) {
      pageColorMap.set(
        ps.pagePath,
        PAGE_COLORS[pageColorMap.size % PAGE_COLORS.length]
      );
    }
  });

  return (
    <Dialog open={Boolean(session)} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        <Box>
          <Typography variant="h6" component="span">
            Session Details
          </Typography>
          <Typography
            variant="body2"
            component="span"
            sx={{
              ml: 2,
              fontFamily: "monospace",
              fontSize: 11,
              color: "text.secondary",
            }}
          >
            {session.sessionId}
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {/* Session Info */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" color="primary" sx={{ mb: 1 }}>
            Session Info
          </Typography>
          <Divider sx={{ mb: 1.5 }} />
          <Stack direction="row" spacing={4} flexWrap="wrap" useFlexGap>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Start Time
              </Typography>
              <Typography variant="body2">
                {dayjs(session.sessionStart).format("MMM D, YYYY HH:mm:ss")}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Duration
              </Typography>
              <Typography variant="body2">
                {formatDuration(totalDurationMs)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Pages Visited
              </Typography>
              <Typography variant="body2">
                {session.pageSessions.length}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Landing Page
              </Typography>
              <Tooltip title={getPageFlow(session)[0] || "-"}>
                <Typography
                  variant="body2"
                  sx={{
                    maxWidth: 200,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {getPageFlow(session)[0] || "-"}
                </Typography>
              </Tooltip>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Persona
              </Typography>
              <Typography variant="body2">{session.persona || "-"}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Experiments
              </Typography>
              <Typography variant="body2" component="div">
                {getExperiments(session).length > 0 ? (
                  <Stack
                    direction="row"
                    spacing={0.5}
                    flexWrap="wrap"
                    useFlexGap
                  >
                    {getExperiments(session).map((exp) => (
                      <Chip
                        key={`${exp.experimentId}:${exp.variantId}`}
                        label={`${exp.experimentId}: ${exp.variantId}`}
                        size="small"
                        variant="outlined"
                        color={
                          exp.variantId === "control" ? "default" : "primary"
                        }
                      />
                    ))}
                  </Stack>
                ) : (
                  "-"
                )}
              </Typography>
            </Box>
          </Stack>
        </Box>

        {/* Traffic Source */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" color="primary" sx={{ mb: 1 }}>
            Traffic Source
          </Typography>
          <Divider sx={{ mb: 1.5 }} />
          <Stack direction="row" spacing={4} flexWrap="wrap" useFlexGap>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Referrer
              </Typography>
              <Tooltip title={session.referrer || "(direct)"}>
                <Typography
                  variant="body2"
                  sx={{
                    maxWidth: 250,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {session.referrer || "(direct)"}
                </Typography>
              </Tooltip>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Link Source
              </Typography>
              <Typography variant="body2">
                {session.linkSource || "-"}
              </Typography>
            </Box>
          </Stack>
        </Box>

        {/* Device & Location */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" color="primary" sx={{ mb: 1 }}>
            Device & Location
          </Typography>
          <Divider sx={{ mb: 1.5 }} />
          <Stack direction="row" spacing={4} flexWrap="wrap" useFlexGap>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Device
              </Typography>
              <Typography variant="body2">
                {session.screenWidth
                  ? session.screenWidth < 768
                    ? "Mobile"
                    : session.screenWidth < 1024
                    ? "Tablet"
                    : "Desktop"
                  : "-"}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Screen Size
              </Typography>
              <Typography variant="body2">
                {session.screenWidth && session.screenHeight
                  ? `${session.screenWidth}×${session.screenHeight}`
                  : "-"}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Country
              </Typography>
              <Typography variant="body2">{session.country || "-"}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Region
              </Typography>
              <Typography variant="body2">{session.region || "-"}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                City
              </Typography>
              <Typography variant="body2">{session.city || "-"}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Timezone
              </Typography>
              <Typography variant="body2">{session.timezone || "-"}</Typography>
            </Box>
            <Box sx={{ minWidth: 200, maxWidth: 400 }}>
              <Typography variant="caption" color="text.secondary">
                User Agent
              </Typography>
              <Tooltip title={session.userAgent || "-"}>
                <Typography
                  variant="body2"
                  sx={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {session.userAgent || "-"}
                </Typography>
              </Tooltip>
            </Box>
          </Stack>
        </Box>

        {/* Conversion & Tracking */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" color="primary" sx={{ mb: 1 }}>
            Conversion & Tracking
          </Typography>
          <Divider sx={{ mb: 1.5 }} />
          <Stack direction="row" spacing={4} flexWrap="wrap" useFlexGap>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Signed Up
              </Typography>
              <Typography variant="body2">
                <Chip
                  label={session.signedUp ? "Yes" : "No"}
                  size="small"
                  color={session.signedUp ? "success" : "default"}
                  variant={session.signedUp ? "filled" : "outlined"}
                />
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Contacted
              </Typography>
              <Typography variant="body2">
                <Chip
                  label={session.contacted ? "Yes" : "No"}
                  size="small"
                  color={session.contacted ? "info" : "default"}
                  variant={session.contacted ? "filled" : "outlined"}
                />
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Email
              </Typography>
              <Typography variant="body2">{session.email || "-"}</Typography>
            </Box>
          </Stack>
        </Box>

        {/* Timeline Visualization */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" color="primary" sx={{ mb: 1 }}>
            Session Timeline
          </Typography>
          <Divider sx={{ mb: 2 }} />

          {/* Timeline bar showing all pages */}
          <Box sx={{ mb: 3 }}>
            <Box
              sx={{
                position: "relative",
                display: "flex",
                height: 40,
                borderRadius: 1,
                overflow: "hidden",
                bgcolor: "grey.100",
              }}
            >
              {session.pageSessions.map((ps, idx) => {
                const startOffset = ps.startTime - sessionStartTime;
                const duration = ps.endTime - ps.startTime;
                const widthPercent =
                  totalDurationMs > 0 ? (duration / totalDurationMs) * 100 : 0;
                const leftPercent =
                  totalDurationMs > 0
                    ? (startOffset / totalDurationMs) * 100
                    : 0;

                return (
                  <Tooltip
                    key={idx}
                    title={
                      <Box>
                        <Typography variant="body2" fontWeight={600}>
                          {ps.pagePath}
                        </Typography>
                        <Typography variant="caption">
                          {formatDuration(duration)} • {ps.events.length} events
                        </Typography>
                      </Box>
                    }
                    arrow
                  >
                    <Box
                      sx={{
                        position: "absolute",
                        left: `${leftPercent}%`,
                        width: `${Math.max(widthPercent, 0.5)}%`,
                        height: "100%",
                        bgcolor: pageColorMap.get(ps.pagePath),
                        opacity: 0.85,
                        transition: "opacity 0.2s",
                        cursor: "pointer",
                        "&:hover": {
                          opacity: 1,
                        },
                      }}
                    />
                  </Tooltip>
                );
              })}
            </Box>
            {/* Time markers */}
            <Box
              sx={{ display: "flex", justifyContent: "space-between", mt: 0.5 }}
            >
              <Typography variant="caption" color="text.secondary">
                {dayjs(session.sessionStart).format("HH:mm:ss")}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {dayjs(session.sessionEnd).format("HH:mm:ss")}
              </Typography>
            </Box>
          </Box>

          {/* Legend */}
          <Stack direction="row" spacing={2} flexWrap="wrap" sx={{ mb: 2 }}>
            {Array.from(pageColorMap.entries()).map(([path, color]) => (
              <Stack
                key={path}
                direction="row"
                alignItems="center"
                spacing={0.5}
              >
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: 0.5,
                    bgcolor: color,
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{
                    maxWidth: 150,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {path}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Box>

        {/* Page Sessions Detail */}
        <Box>
          <Typography variant="subtitle2" color="primary" sx={{ mb: 1 }}>
            Page Sessions ({session.pageSessions.length})
          </Typography>
          <Divider sx={{ mb: 2 }} />

          <Stack spacing={2}>
            {session.pageSessions.map((ps, idx) => {
              const duration = ps.endTime - ps.startTime;
              const pageColor = pageColorMap.get(ps.pagePath) || "#666";
              const viewEvents = ps.events.filter((e) => e.type === "view");
              const interactionEvents = ps.events.filter(
                (e) => e.type === "interaction"
              );

              return (
                <Paper
                  key={idx}
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderLeft: 4,
                    borderLeftColor: pageColor,
                  }}
                >
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="flex-start"
                    sx={{ mb: 1 }}
                  >
                    <Box>
                      <Typography variant="subtitle2" fontWeight={600}>
                        {idx + 1}. {ps.pagePath}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {dayjs(ps.startTime).format("HH:mm:ss")} -{" "}
                        {dayjs(ps.endTime).format("HH:mm:ss")} •{" "}
                        {formatDuration(duration)}
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={1}>
                      {viewEvents.length > 0 && (
                        <Chip
                          label={`${viewEvents.length} view${
                            viewEvents.length > 1 ? "s" : ""
                          }`}
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: 11 }}
                        />
                      )}
                      {interactionEvents.length > 0 && (
                        <Chip
                          label={`${interactionEvents.length} click${
                            interactionEvents.length > 1 ? "s" : ""
                          }`}
                          size="small"
                          color="primary"
                          sx={{ fontSize: 11 }}
                        />
                      )}
                    </Stack>
                  </Stack>

                  {/* Events timeline within page */}
                  {ps.events.length > 0 && (
                    <Box
                      sx={{
                        mt: 1,
                        pl: 2,
                        borderLeft: 2,
                        borderColor: "grey.200",
                      }}
                    >
                      <Stack spacing={0.5}>
                        {ps.events.map((event, eventIdx) => (
                          <Box
                            key={eventIdx}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ minWidth: 60, fontFamily: "monospace" }}
                            >
                              {dayjs(
                                event.type === "view"
                                  ? event.startTime
                                  : event.time
                              ).format("HH:mm:ss")}
                            </Typography>
                            {event.type === "view" ? (
                              <>
                                <Chip
                                  label="view"
                                  size="small"
                                  sx={{
                                    fontSize: 10,
                                    height: 18,
                                    bgcolor: "info.light",
                                    color: "info.contrastText",
                                  }}
                                />
                                <Typography variant="caption">
                                  <strong>{event.elementId}</strong> (
                                  {formatDuration(
                                    event.endTime - event.startTime
                                  )}
                                  )
                                </Typography>
                              </>
                            ) : (
                              <>
                                <Chip
                                  label={event.iType}
                                  size="small"
                                  sx={{
                                    fontSize: 10,
                                    height: 18,
                                    bgcolor: "warning.light",
                                    color: "warning.contrastText",
                                  }}
                                />
                                <Typography variant="caption">
                                  <strong>{event.elementId}</strong>
                                </Typography>
                              </>
                            )}
                          </Box>
                        ))}
                      </Stack>
                    </Box>
                  )}
                </Paper>
              );
            })}
          </Stack>
        </Box>
      </DialogContent>
      {onDelete && (
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={handleDelete}
            disabled={deleting}
          >
            {deleting ? "Deleting..." : "Delete Session"}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};
